import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { ChecklistKeyword, ChecklistPdf, ChecklistQuestionSpec } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import { mutate } from "swr"
import useSWRMutation from "swr/mutation"

export const invalidateChecklists = () =>
    Promise.all([
        mutate((key) => Array.isArray(key) && key[0] === "get-checklist"),
        invalidatePilotTrackingQuery("checklists"),
    ])

const createChecklist = ({
    name,
    description,
    form,
    orgId,
}: {
    orgId: string
    name: string
    description: string | null | undefined
    form: ChecklistQuestionSpec[]
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/checklist", {
        body: { name, description, form },
        params: { orgId },
    })
}

export const useCreateChecklist = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-checklist",
        (
            key,
            {
                arg,
            }: {
                arg: {
                    name: string
                    description: string | null | undefined
                    form: ChecklistQuestionSpec[]
                }
            }
        ) =>
            orgId
                ? createChecklist({
                      name: arg.name,
                      description: arg.description,
                      form: arg.form,
                      orgId,
                  })
                : undefined,
        { onSuccess: invalidateChecklists }
    )

    return { createChecklist: trigger, isMutating }
}

const deleteChecklist = ({ checklistId, orgId }: { orgId: string; checklistId: string }) => {
    return fetchApiGateway("delete /org/:orgId/fleet/checklist/:checklistId", {
        params: { checklistId, orgId },
    })
}

export const useDeleteChecklist = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-checklist",
        (key, { arg: checklistId }: { arg: string }) =>
            orgId ? deleteChecklist({ checklistId, orgId }) : undefined,
        { onSuccess: invalidateChecklists }
    )

    return { deleteChecklist: trigger, isMutating }
}

const getChecklist = ({ checklistId, orgId }: { orgId: string; checklistId: string }) => {
    return fetchApiGateway("get /org/:orgId/fleet/checklist/:checklistId", {
        params: { orgId, checklistId },
    })
}

export const useChecklist = ({
    checklistId,
    orgId: orgIdParam,
}: {
    checklistId: string
    orgId?: string
}) => {
    const activeOrgId = useAppSelector(selectActiveWorkspaceId())

    const orgId = orgIdParam ?? activeOrgId

    const { data, isLoading } = useSWRNoRevalidate(["get-checklist", orgId, checklistId], () =>
        orgId ? getChecklist({ checklistId, orgId }) : undefined
    )

    return { data, isLoading }
}

const generateChecklist = ({
    prompt,
    keywords,
    pdf,
    orgId,
}: {
    prompt?: string
    keywords?: ChecklistKeyword[]
    pdf?: ChecklistPdf
    orgId: string
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/checklist/generate", {
        body: { message: prompt, keywords, pdf },
        params: { orgId },
    })
}

export const useGenerateAiChecklist = () => {
    const activeOrgId = useAppSelector(selectActiveWorkspaceId())

    const { data, trigger } = useSWRMutation(
        "generate-checklist",
        (
            key,
            { arg }: { arg: { prompt?: string; keywords?: ChecklistKeyword[]; pdf?: ChecklistPdf } }
        ) =>
            activeOrgId
                ? generateChecklist({
                      prompt: arg.prompt,
                      keywords: arg.keywords,
                      pdf: arg.pdf,
                      orgId: activeOrgId,
                  })
                : undefined,
        { onSuccess: invalidateChecklists }
    )

    return { data, generateChecklist: trigger }
}

const updateChecklist = ({
    checklistId,
    orgId,
    name,
    description,
    form,
}: {
    checklistId: string
    orgId: string
    name?: string
    description?: string | null
    form?: ChecklistQuestionSpec[]
}) => {
    return fetchApiGateway("patch /org/:orgId/fleet/checklist/:checklistId", {
        body: { name, description, form },
        params: { orgId, checklistId },
    })
}

export const useUpdateChecklist = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-checklist",
        (
            key,
            {
                arg,
            }: {
                arg: {
                    checklistId: string
                    name?: string
                    description?: string | null
                    form?: ChecklistQuestionSpec[]
                }
            }
        ) => (orgId ? updateChecklist({ ...arg, orgId }) : undefined),
        { onSuccess: invalidateChecklists }
    )

    return { updateChecklist: trigger, isMutating }
}
